/**
 * Address Heatmap Viewer
 *
 * Responsive viewer for address-map and heatmap visual artifacts.
 *
 * Primary responsibilities:
 * - Fit address coordinates and heatmap points into a local viewport
 * - Render markers, labels, addresses, and density circles
 * - Present loading, empty, and error states without map dependencies
 *
 * Dependencies:
 * - React
 * - Visual artifact types
 * - Geo tile map helpers
 */

'use client'

import { type RefObject, useEffect, useMemo, useRef, useState } from 'react'
import type {
  AddressMapArtifact,
  AddressMapMarker,
  HeatmapArtifact,
  HeatmapPoint
} from '../../lib/visual-artifacts'
import {
  GeoTileLayer,
  createGeoTileMap,
  getGeoBounds,
  projectGeoCoordinate,
  type GeoTileMap
} from '@/components/visual-artifacts/geo-tile-map'

export type AddressHeatmapArtifact = AddressMapArtifact | HeatmapArtifact

export type AddressHeatmapViewerProps = {
  artifact?: AddressHeatmapArtifact | null
  isLoading?: boolean
  error?: string | null
  className?: string
}

type Viewport = {
  width: number
  height: number
}

type Point2D = {
  x: number
  y: number
}

type PositionedMarker = {
  marker: AddressMapMarker
  point: Point2D
  labelAnchor: 'start' | 'middle' | 'end'
  labelPoint: Point2D
}

type PositionedHeatPoint = {
  point: HeatmapPoint
  viewportPoint: Point2D
  radius: number
  color: string
  opacity: number
}

type FitTransform = {
  scale: number
  offsetX: number
  offsetY: number
  fittedWidth: number
  fittedHeight: number
}

type ViewerStateProps = {
  title: string
  message: string
}

type RgbColor = {
  red: number
  green: number
  blue: number
}

const defaultViewport: Viewport = {
  width: 720,
  height: 420
}

const heatmapPadding = 28
const emptyArtifactMessage = 'No map or heatmap data is available yet.'
const labelOffsets = [
  { x: 22, y: -30 },
  { x: -22, y: -30 },
  { x: 22, y: 40 },
  { x: -22, y: 40 },
  { x: 0, y: -48 },
  { x: 0, y: 54 }
] as const

// --------------

export default function AddressHeatmapViewer({
  artifact,
  isLoading = false,
  error = null,
  className
}: AddressHeatmapViewerProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [viewport, setViewport] = useState<Viewport>(defaultViewport)
  const [isCanvasAvailable, setIsCanvasAvailable] = useState(true)

  useEffect(() => {
    const element = viewportRef.current

    if (!element) {
      return
    }

    const updateViewport = () => {
      const rect = element.getBoundingClientRect()

      const nextViewport = {
        width: Math.max(Math.round(rect.width), 1),
        height: Math.max(Math.round(rect.height), 1)
      }

      setViewport((currentViewport) =>
        currentViewport.width === nextViewport.width &&
        currentViewport.height === nextViewport.height
          ? currentViewport
          : nextViewport
      )
    }

    updateViewport()

    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(updateViewport)
    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  const addressMarkers = useMemo(
    () => (artifact?.type === 'address-map' ? positionMarkers(artifact, viewport) : []),
    [artifact, viewport]
  )

  const heatmapPoints = useMemo(
    () => (artifact?.type === 'heatmap' ? positionHeatmapPoints(artifact, viewport) : []),
    [artifact, viewport]
  )

  useEffect(() => {
    if (artifact?.type !== 'heatmap') {
      return
    }

    const canvas = canvasRef.current

    if (!canvas) {
      return
    }

    const context = canvas.getContext('2d')

    if (!context) {
      setIsCanvasAvailable(false)
      return
    }

    setIsCanvasAvailable(true)

    const pixelRatio = window.devicePixelRatio || 1
    canvas.width = Math.round(viewport.width * pixelRatio)
    canvas.height = Math.round(viewport.height * pixelRatio)
    canvas.style.width = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`

    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    context.clearRect(0, 0, viewport.width, viewport.height)

    heatmapPoints.forEach((heatPoint) => {
      drawHeatCircle(context, heatPoint)
    })
  }, [artifact, heatmapPoints, viewport])

  const wrapperClassName = joinClassNames(
    'overflow-hidden rounded-lg border border-border bg-background text-foreground shadow-sm',
    className
  )

  if (isLoading) {
    return (
      <section className={wrapperClassName} aria-busy="true">
        <ViewerState title="Loading visual artifact" message="Preparing the map view." />
      </section>
    )
  }

  if (error) {
    return (
      <section className={wrapperClassName} role="alert">
        <ViewerState title="Map unavailable" message={error} />
      </section>
    )
  }

  if (!artifact) {
    return (
      <section className={wrapperClassName}>
        <ViewerState title="Nothing to show" message={emptyArtifactMessage} />
      </section>
    )
  }

  if (!hasRenderableData(artifact)) {
    return (
      <section className={wrapperClassName}>
        <ViewerState title="Nothing to show" message={emptyArtifactMessage} />
      </section>
    )
  }

  const summary =
    artifact.type === 'address-map'
      ? `${artifact.data.markers.length} addresses`
      : `${artifact.data.points.length} heat points`

  return (
    <section className={wrapperClassName}>
      <div className="border-border flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold">{artifact.title}</h3>
          <p className="text-muted-foreground text-xs">
            {artifact.type === 'address-map' ? 'Address map' : 'Density heatmap'}
          </p>
        </div>
        <span className="border-border text-muted-foreground rounded-full border px-2.5 py-1 text-xs">
          {summary}
        </span>
      </div>

      <div ref={viewportRef} className="relative h-[420px] min-h-[320px] w-full overflow-hidden bg-slate-50">
        {artifact.type === 'address-map' ? (
          <AddressMapLayer artifact={artifact} markers={addressMarkers} viewport={viewport} />
        ) : (
          <HeatmapLayer
            artifact={artifact}
            canvasRef={canvasRef}
            isCanvasAvailable={isCanvasAvailable}
            points={heatmapPoints}
            viewport={viewport}
          />
        )}
      </div>

      {artifact.type === 'address-map' ? (
        <AddressList markers={artifact.data.markers} />
      ) : (
        <HeatmapSummary artifact={artifact} />
      )}
    </section>
  )
}

// --------------

function AddressMapLayer({
  artifact,
  markers,
  viewport
}: {
  artifact: AddressMapArtifact
  markers: PositionedMarker[]
  viewport: Viewport
}) {
  const tileMap = useMemo(() => createAddressTileMap(artifact, viewport), [artifact, viewport])

  return (
    <svg
      className="absolute inset-0 size-full"
      role="img"
      aria-label={`${artifact.title} address map`}
      viewBox={`0 0 ${viewport.width} ${viewport.height}`}
      preserveAspectRatio="none"
    >
      <GeoTileLayer tileMap={tileMap} />
      <path
        d={buildMarkerPath(markers)}
        fill="none"
        stroke="#0f766e"
        strokeDasharray="7 9"
        strokeLinecap="round"
        strokeOpacity="0.65"
        strokeWidth="2.5"
      />

      {markers.map(({ marker, point, labelAnchor, labelPoint }) => (
        <g key={marker.id} transform={`translate(${point.x} ${point.y})`}>
          <title>{`${marker.label}: ${marker.address}`}</title>
          <circle r="13" fill="#0f766e" opacity="0.16" />
          <circle r="7" fill="#0f766e" stroke="#ffffff" strokeWidth="2" />
          <line
            x1="0"
            y1="0"
            x2={labelPoint.x - point.x}
            y2={labelPoint.y - point.y + 8}
            stroke="#0f766e"
            strokeOpacity="0.72"
            strokeWidth="1.5"
          />
          <text
            x={labelPoint.x - point.x}
            y={labelPoint.y - point.y}
            fill="#0f172a"
            fontSize="12"
            fontWeight="700"
            paintOrder="stroke"
            stroke="#ffffff"
            strokeWidth="4"
            textAnchor={labelAnchor}
          >
            {truncateText(marker.label, 28)}
          </text>
          <text
            x={labelPoint.x - point.x}
            y={labelPoint.y - point.y + 15}
            fill="#334155"
            fontSize="10"
            paintOrder="stroke"
            stroke="#ffffff"
            strokeWidth="3"
            textAnchor={labelAnchor}
          >
            {truncateText(marker.address, 42)}
          </text>
        </g>
      ))}
    </svg>
  )
}

function HeatmapLayer({
  artifact,
  canvasRef,
  isCanvasAvailable,
  points,
  viewport
}: {
  artifact: HeatmapArtifact
  canvasRef: RefObject<HTMLCanvasElement>
  isCanvasAvailable: boolean
  points: PositionedHeatPoint[]
  viewport: Viewport
}) {
  const fallbackOpacity = isCanvasAvailable ? 0.14 : 0.42

  return (
    <>
      <svg
        className="absolute inset-0 size-full"
        aria-hidden="true"
        viewBox={`0 0 ${viewport.width} ${viewport.height}`}
        preserveAspectRatio="none"
      >
        <HeatmapSurface viewport={viewport} />
      </svg>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 size-full mix-blend-multiply"
        aria-hidden="true"
      />
      <svg
        className="absolute inset-0 size-full"
        role="img"
        aria-label={`${artifact.title} heatmap`}
        viewBox={`0 0 ${viewport.width} ${viewport.height}`}
        preserveAspectRatio="none"
      >
        {points.map((heatPoint) => {
          const labelPoint = getHeatLabelPoint(heatPoint, viewport)

          return (
            <g key={getHeatPointKey(heatPoint)}>
              <title>
                {`x ${Math.round(heatPoint.point.x)}, y ${Math.round(
                  heatPoint.point.y
                )}, intensity ${formatPercent(heatPoint.point.intensity)}`}
              </title>
              <circle
                cx={heatPoint.viewportPoint.x}
                cy={heatPoint.viewportPoint.y}
                r={heatPoint.radius}
                fill={heatPoint.color}
                opacity={fallbackOpacity}
              />
              <circle
                cx={heatPoint.viewportPoint.x}
                cy={heatPoint.viewportPoint.y}
                r="4"
                fill="#0f766e"
                stroke="#ffffff"
                strokeWidth="2"
              />
              <text
                x={labelPoint.x}
                y={labelPoint.y}
                fill="#0f172a"
                fontSize="12"
                fontWeight="700"
                paintOrder="stroke"
                stroke="#ffffff"
                strokeWidth="4"
              >
                {formatPercent(heatPoint.point.intensity)}
              </text>
            </g>
          )
        })}
      </svg>
    </>
  )
}

function HeatmapSurface({ viewport }: { viewport: Viewport }) {
  const gridSize = 56
  const verticalLines = Math.ceil(viewport.width / gridSize)
  const horizontalLines = Math.ceil(viewport.height / gridSize)

  return (
    <g>
      <rect width={viewport.width} height={viewport.height} fill="#f8fafc" />
      {Array.from({ length: verticalLines + 1 }).map((_, index) => (
        <line
          key={`heat-v-${index}`}
          x1={index * gridSize}
          x2={index * gridSize}
          y1="0"
          y2={viewport.height}
          stroke="#dbe4ee"
          strokeWidth="1"
        />
      ))}
      {Array.from({ length: horizontalLines + 1 }).map((_, index) => (
        <line
          key={`heat-h-${index}`}
          x1="0"
          x2={viewport.width}
          y1={index * gridSize}
          y2={index * gridSize}
          stroke="#dbe4ee"
          strokeWidth="1"
        />
      ))}
      <rect
        x="14"
        y="14"
        width={Math.max(viewport.width - 28, 1)}
        height={Math.max(viewport.height - 28, 1)}
        rx="16"
        fill="none"
        stroke="#94a3b8"
        strokeDasharray="6 9"
        strokeWidth="1.5"
      />
    </g>
  )
}

function AddressList({ markers }: { markers: AddressMapMarker[] }) {
  return (
    <div className="border-border bg-background grid gap-2 border-t px-4 py-3 sm:grid-cols-2">
      {markers.map((marker) => (
        <div key={marker.id} className="min-w-0">
          <p className="truncate text-xs font-semibold">{marker.label}</p>
          <p className="text-muted-foreground truncate text-xs">{marker.address}</p>
        </div>
      ))}
    </div>
  )
}

function HeatmapSummary({ artifact }: { artifact: HeatmapArtifact }) {
  const strongestPoint = artifact.data.points.reduce((strongest, point) =>
    point.intensity > strongest.intensity ? point : strongest
  )

  return (
    <div className="border-border bg-background text-muted-foreground flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 text-xs">
      <span>
        Bounds {artifact.data.bounds.width} x {artifact.data.bounds.height}
      </span>
      <span>Peak density {formatPercent(strongestPoint.intensity)}</span>
    </div>
  )
}

function ViewerState({ title, message }: ViewerStateProps) {
  return (
    <div className="flex min-h-[320px] items-center justify-center px-6 py-12 text-center">
      <div className="max-w-sm">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-muted-foreground mt-1 text-sm">{message}</p>
      </div>
    </div>
  )
}

// --------------

function hasRenderableData(artifact: AddressHeatmapArtifact) {
  if (artifact.type === 'address-map') {
    return artifact.data.markers.length > 0
  }

  return artifact.data.points.length > 0
}

function positionMarkers(artifact: AddressMapArtifact, viewport: Viewport): PositionedMarker[] {
  const tileMap = createAddressTileMap(artifact, viewport)

  return artifact.data.markers.map((marker, index) => {
    const point = projectGeoCoordinate(marker.coordinate, tileMap)
    const offset = getLabelOffset(index, point, viewport)
    const labelPoint = {
      x: clamp(point.x + offset.x, 12, viewport.width - 12),
      y: clamp(point.y + offset.y, 22, viewport.height - 22)
    }
    const labelAnchor =
      Math.abs(offset.x) < 1 ? 'middle' : offset.x > 0 ? 'start' : 'end'

    return {
      marker,
      point,
      labelAnchor,
      labelPoint
    }
  })
}

function buildMarkerPath(markers: PositionedMarker[]) {
  if (markers.length < 2) {
    return ''
  }

  return markers
    .map(({ point }, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ')
}

// --------------

function createAddressTileMap(
  artifact: AddressMapArtifact,
  viewport: Viewport
): GeoTileMap {
  const bounds = getGeoBounds(
    [artifact.data.center, ...artifact.data.markers.map((marker) => marker.coordinate)],
    artifact.data.center
  )

  return createGeoTileMap(bounds, viewport)
}

function getLabelOffset(index: number, point: Point2D, viewport: Viewport) {
  const baseOffset = labelOffsets[index % labelOffsets.length]
  const nearLeft = point.x < viewport.width * 0.22
  const nearRight = point.x > viewport.width * 0.78
  const nearTop = point.y < viewport.height * 0.24
  const nearBottom = point.y > viewport.height * 0.76
  const x = nearLeft
    ? Math.abs(baseOffset.x) || 24
    : nearRight
      ? -Math.abs(baseOffset.x) || -24
      : baseOffset.x
  const y = nearTop
    ? Math.abs(baseOffset.y)
    : nearBottom
      ? -Math.abs(baseOffset.y)
      : baseOffset.y

  return {
    x,
    y
  }
}

function positionHeatmapPoints(
  artifact: HeatmapArtifact,
  viewport: Viewport
): PositionedHeatPoint[] {
  const transform = getFitTransform(
    artifact.data.bounds.width,
    artifact.data.bounds.height,
    viewport,
    heatmapPadding
  )
  const radiusBase = clamp(Math.min(transform.fittedWidth, transform.fittedHeight) * 0.12, 30, 92)

  return artifact.data.points.map((point) => {
    const intensity = clamp(point.intensity, 0, 1)

    return {
      point,
      viewportPoint: {
        x: transform.offsetX + point.x * transform.scale,
        y: transform.offsetY + point.y * transform.scale
      },
      radius: radiusBase * (0.65 + intensity * 0.85),
      color: getHeatColor(intensity),
      opacity: 0.2 + intensity * 0.48
    }
  })
}

function getHeatLabelPoint(heatPoint: PositionedHeatPoint, viewport: Viewport) {
  return {
    x: clamp(heatPoint.viewportPoint.x + 10, 18, viewport.width - 40),
    y: clamp(heatPoint.viewportPoint.y - 10, 22, viewport.height - 18)
  }
}

function getFitTransform(
  sourceWidth: number,
  sourceHeight: number,
  viewport: Viewport,
  padding: number
): FitTransform {
  const safeSourceWidth = Math.max(sourceWidth, 1)
  const safeSourceHeight = Math.max(sourceHeight, 1)
  const availableWidth = Math.max(viewport.width - padding * 2, 1)
  const availableHeight = Math.max(viewport.height - padding * 2, 1)
  const scale = Math.min(availableWidth / safeSourceWidth, availableHeight / safeSourceHeight)
  const fittedWidth = safeSourceWidth * scale
  const fittedHeight = safeSourceHeight * scale

  return {
    scale,
    fittedWidth,
    fittedHeight,
    offsetX: (viewport.width - fittedWidth) / 2,
    offsetY: (viewport.height - fittedHeight) / 2
  }
}

function drawHeatCircle(context: CanvasRenderingContext2D, heatPoint: PositionedHeatPoint) {
  const { viewportPoint, radius, opacity } = heatPoint
  const rgb = getHeatRgb(heatPoint.point.intensity)
  const gradient = context.createRadialGradient(
    viewportPoint.x,
    viewportPoint.y,
    0,
    viewportPoint.x,
    viewportPoint.y,
    radius
  )

  gradient.addColorStop(0, toRgba(rgb, opacity))
  gradient.addColorStop(0.55, toRgba(rgb, opacity * 0.42))
  gradient.addColorStop(1, toRgba(rgb, 0))

  context.fillStyle = gradient
  context.beginPath()
  context.arc(viewportPoint.x, viewportPoint.y, radius, 0, Math.PI * 2)
  context.fill()
}

function getHeatColor(intensity: number) {
  const { red, green, blue } = getHeatRgb(intensity)

  return `rgb(${red}, ${green}, ${blue})`
}

function getHeatRgb(intensity: number): RgbColor {
  if (intensity >= 0.72) {
    return {
      red: 220,
      green: 38,
      blue: 38
    }
  }

  if (intensity >= 0.42) {
    return {
      red: 249,
      green: 115,
      blue: 22
    }
  }

  return {
    red: 234,
    green: 179,
    blue: 8
  }
}

function toRgba({ red, green, blue }: RgbColor, alpha: number) {
  return `rgba(${red}, ${green}, ${blue}, ${clamp(alpha, 0, 1)})`
}

function getHeatPointKey({ point }: PositionedHeatPoint) {
  return `${point.x}:${point.y}:${point.intensity}`
}

// --------------

function truncateText(value: string, maxLength: number) {
  if (value.length <= maxLength) {
    return value
  }

  return `${value.slice(0, Math.max(maxLength - 3, 0))}...`
}

function formatPercent(value: number) {
  return `${Math.round(clamp(value, 0, 1) * 100)}%`
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function joinClassNames(...values: Array<string | undefined>) {
  return values.filter((value): value is string => Boolean(value)).join(' ')
}
